import { useLocalStorage, useSessionStorage } from '@vueuse/core';
import type { ConfirmSignUpOutput, ResetPasswordInput, ResetPasswordOutput, SignUpOutput, UpdatePasswordInput } from 'aws-amplify/auth';
import { isEmpty } from 'lodash-es';
import { DateTime } from 'luxon';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { Observable, switchMap, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { ComputedRef, type Ref, computed, ref } from 'vue';
import type { RouteLocationNamedRaw } from 'vue-router';

import {
	AuthToken,
	ConfirmResetPasswordPayload,
	ConfirmSignUpPayload,
	SignInPayload,
	SignUpPayload,
	UserDto,
	confirmResetPassword as confirmResetPasswordFn,
	confirmSignUp as confirmSignUpFn,
	fetchAuthSession as fetchAuthSessionFn,
	fetchProfile,
	requestJWTToken,
	resetPassword as resetPasswordFn,
	signIn as signInFn,
	signOut as signOutFn,
	signUp as signUpFn,
	updatePassword as updatePasswordFn
} from '~/services';
import { useBackendHttpService } from '~/services/http';
import { usePrimordialSoupStore } from '~/stores/primordial-soup.store.ts';
import { NOT_ASSIGNED, computeFullName, fromBase64Url } from '~/utils';

export type AuthStoreOutputs = {
	/** Refs and computed */
	authSession: Ref<any>;
	idToken: Ref<string>;
	impersonated: Ref<boolean>;
	isAuthenticated: ComputedRef<boolean>;
	userPartnerId: ComputedRef<string>;
	userFullName: ComputedRef<string>;
	userPartnerLabel: ComputedRef<string>;
	userLastRouteBeforeSettings: Ref<RouteLocationNamedRaw>;
	userPartnerCreationDate: ComputedRef<DateTime>;
	userProfile: Ref<UserDto>;

	/** Functions */
	confirmSignUp: (payload: ConfirmSignUpPayload) => Observable<ConfirmSignUpOutput>;
	confirmResetPassword: (payload: ConfirmResetPasswordPayload) => Observable<void>;
	fetchAuthenticatedUserProfile: () => Observable<UserDto>;
	fetchAuthSession: () => Observable<UserDto>;
	fetchAuthToken: (code: string) => Observable<AuthToken>;
	resetAuth: () => void;
	resetPassword: (payload: ResetPasswordInput) => Observable<ResetPasswordOutput>;
	setImpersonation: (isImpersonated: boolean, token: string) => void;
	setLastRouteBeforeSettings: (route: RouteLocationNamedRaw) => void;
	signIn: (payload: SignInPayload) => Observable<UserDto>;
	signUp: (payload: SignUpPayload) => Observable<SignUpOutput>;
	signOut: () => Observable<void>;
	updatePassword: (payload: UpdatePasswordInput) => Observable<void>;
	validateTokenExpiration: (session: any) => boolean;
};

export const useAuthStore = defineStore<string, AuthStoreOutputs>('auth', (): AuthStoreOutputs => {
	/** Imports other stores */
	const primordialSoupStore = usePrimordialSoupStore();

	/** Auth refs and computed */
	const idToken: Ref<string> = useLocalStorage('ce-id-token', null);
	const impersonated = useSessionStorage<boolean>('ce-impersonated', false);
	const isAuthenticated = computed(() => userProfile.value != null && !isEmpty(userProfile.value));
	const userProfile = ref<UserDto>();
	const authSession = ref<any>();
	const userLastRouteBeforeSettings = ref<RouteLocationNamedRaw>(); // When leaving settings we w, authSession.value != null && !isEmpty(authSession.value)ant to go back to this saved route

	const userFullName = computed(() => (userProfile.value ? computeFullName(userProfile.value) : NOT_ASSIGNED));
	const userPartnerId = computed(() => userProfile.value?.organizationId);
	const userPartnerLabel = computed(() => userProfile.value?.organizationLabel);
	const userPartnerCreationDate = computed(() =>
		userProfile.value?.organizationCreatedAt ? DateTime.fromMillis(userProfile.value.organizationCreatedAt) : null
	);

	/** Auth functions */
	function setImpersonation(isImpersonated: boolean, token: string): void {
		impersonated.value = isImpersonated;
		setAuthorizationBearer(token);
	}

	function setAuthSession(session: any): void {
		authSession.value = session;
		impersonated.value = false;
		setTokensFromSession(session);
	}

	function validateTokenExpiration(session: any): boolean {
		const idTokenFromSession = session?.tokens?.idToken;
		let isValid = false;

		if (idTokenFromSession) {
			const { exp: expirationUnixTimestamp } = idTokenFromSession?.payload ?? {};
			const now = Date.now();
			return now < expirationUnixTimestamp * 1000;
		}

		return isValid;
	}

	function setAuthorizationBearer(token: string) {
		if (token) {
			idToken.value = token;
			const { addDefaultHeaders } = useBackendHttpService();
			addDefaultHeaders({ Authorization: `Bearer ${token}` });
		}
	}

	function resetAuth(): void {
		impersonated.value = false;
		idToken.value = null;
		userProfile.value = null;
		authSession.value = null;
		// Reset app context
		primordialSoupStore.reset();
	}

	function setTokensFromSession(session: any): void {
		const idTokenFromSession = session?.tokens?.idToken?.toString();
		setAuthorizationBearer(idTokenFromSession);
	}

	function fetchAuthenticatedUserProfile(): Observable<UserDto> {
		return fetchProfile().pipe(
			tap(authenticatedUser => {
				userProfile.value = authenticatedUser;
			})
		);
	}

	function fetchAuthSession(): Observable<UserDto> {
		return fetchAuthSessionFn().pipe(
			map(authSession => {
				// FIXME: understand why fetchAuthSession sends an empty authSession instead of an error ? Feels weird.
				if (validateTokenExpiration(authSession)) {
					setAuthSession(authSession);
					return authSession;
				}
				throw new Error('failed to refresh auth session');
			}),
			switchMap(() => {
				return fetchAuthenticatedUserProfile();
			})
		);
	}

	function signIn(payload: SignInPayload) {
		return signInFn(payload).pipe(
			switchMap(() => {
				return fetchAuthSession();
			})
		);
	}

	function signOut(): Observable<void> {
		return signOutFn().pipe(tap(resetAuth));
	}

	function signUp(payload: SignUpPayload): Observable<SignUpOutput> {
		return signUpFn(payload);
	}

	function confirmSignUp({ email, code }: ConfirmSignUpPayload): Observable<ConfirmSignUpOutput> {
		const confirmSignUpInput = { username: fromBase64Url(email), confirmationCode: code };
		return confirmSignUpFn(confirmSignUpInput);
	}

	function resetPassword(payload: ResetPasswordInput): Observable<ResetPasswordOutput> {
		return resetPasswordFn(payload);
	}

	function confirmResetPassword({ email, code, password }: ConfirmResetPasswordPayload): Observable<void> {
		const confirmResetPasswordInput = { username: fromBase64Url(email), confirmationCode: code, newPassword: password };

		return confirmResetPasswordFn(confirmResetPasswordInput);
	}

	function updatePassword(payload: UpdatePasswordInput): Observable<void> {
		return updatePasswordFn(payload);
	}

	function fetchAuthToken(code: string): Observable<AuthToken> {
		return requestJWTToken(code).pipe(tap(token => setImpersonation(true, token.jwt)));
	}

	// Specific UX: before going to "settings", we save user's last visited route.
	// This allows us to go back to this saved route when leaving settings.
	function setLastRouteBeforeSettings(route: RouteLocationNamedRaw): void {
		userLastRouteBeforeSettings.value = route;
	}

	return {
		/** Refs and computed */
		authSession,
		idToken,
		impersonated,
		isAuthenticated,
		userPartnerId,
		userFullName,
		userPartnerLabel,
		userLastRouteBeforeSettings,
		userPartnerCreationDate,
		userProfile,

		/** Functions */
		confirmSignUp,
		confirmResetPassword,
		fetchAuthenticatedUserProfile,
		fetchAuthSession,
		fetchAuthToken,
		resetAuth,
		resetPassword,
		setImpersonation,
		setLastRouteBeforeSettings,
		signIn,
		signUp,
		signOut,
		updatePassword,
		validateTokenExpiration
	};
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
